<script setup lang="ts">
import ModalWrap from '@/components/common/ModalWrap.vue'

import { useCopy } from '@/components/hooks/useCopy'

import { useMobile } from '@/components/hooks/useMobile'
import { useAccountStore } from '@/store/account'
import { config } from '@/store/contract'

import { useStakeStore } from '@/store/stake'
import { compareAddress, getAccountAddress, getImageUrl } from '@/utils'

import { useAppKit, useAppKitAccount, useAppKitProvider, useDisconnect } from '@reown/appkit/vue'

import { BrowserProvider } from 'ethers'
import { round } from 'lodash'
import { Button, Divider, SelectButton } from 'primevue'
import { computed, onMounted, ref } from 'vue'

import SwitchChainButton from './SwitchChainButton.vue'

const { open } = useAppKit()
const { isMobile } = useMobile()
const accountData = useAppKitAccount()
const { disconnect } = useDisconnect()
const stakeStore = useStakeStore()
const accountStore = useAccountStore()
const blockExplorerUrl = computed(
  () =>
    `${accountStore.currentNetworkData?.blockExplorers?.default?.url}/address/${accountData.value.address}`,
)

const accountModal = computed(() => accountStore.accountModal)
const price = computed(() => accountStore.price)
const isCcip = computed(() => accountStore.contractHelper?.adapter.isCcip)
const addressData = computed(() => {
  const chain = isCcip.value ? 'L2' : 'L1'
  return config[chain]?.contracts
})

const userInfo = computed(() => accountStore.userInfo)
const tokens = computed(() => [
  {
    title: isCcip.value ? 'WASTR' : 'ASTR',
    symbol: isCcip.value ? 'SBY' : '',
    balance: round(Number(userInfo.value?.balances.WASTR), 4),
    price: round(Number(userInfo.value?.balances.WASTR) * price.value, 4),
    change: '',
    icon: 'astr.svg',
    address: isCcip.value ? addressData.value.WASTR : '',
  },
  {
    title: 'wstASTR',
    symbol: 'wstASTR',
    balance: round(Number(userInfo.value?.balances.WstASTR), 4),
    price: round(Number(userInfo.value?.balances.WstASTR) * price.value, 4),
    change: '',
    icon: 'stastr.svg',
    address: addressData.value.WstASTR,
  },
])
const { copyText } = useCopy()
const txList = computed(() =>
  stakeStore.txList?.filter((item) => {
    if (!compareAddress(item.account, accountStore.account)) {
      return false
    }
    const activeChain = item.chainId === accountStore.currentNetworkChainId
    if (isCcip.value) {
      return activeChain
    }
    return ['unstake', 'bridge'].includes(item.type) && activeChain
  })
    .slice(0, 10),
)
const options = ref([
  { label: 'Funds in wallet', value: 'wallet' },
  { label: 'Tx history', value: 'history' },
])
onMounted(async () => {
})

function copyAddress() {
  if (accountData.value.address) {
    copyText(accountData.value.address)
    // 可以添加一个复制成功的提示
  }
}

function handleDisconnect() {
  disconnect()
  accountStore.toggleModalVisible()
}

function handleModal() {
  accountStore.toggleModalVisible()
  accountStore.changeModal('wallet')
}
// function handleModalTx() {
//   accountStore.toggleModalVisible()
//   accountStore.changeModal('history')
// }
async function getProvider() {
  const { walletProvider } = useAppKitProvider('eip155')
  const ethersProvider = new BrowserProvider(walletProvider as any)
  return ethersProvider
}

async function addToWallet(token: any) {
  try {
    const provider = await getProvider()
    const signer = await provider.getSigner()

    await signer.provider.send('wallet_watchAsset', {
      type: 'ERC20',
      options: {
        address: token.address,
        symbol: token.symbol,
        decimals: 18,
        // image: `${window.location.origin}/images/tokens/${token.icon}` // 可选：添加代币图标
      },
    })
  }
  catch (error) {
    console.error('Failed to add token to wallet:', error)
  }
}

console.log('accountData', accountData)
</script>

<template>
  <div class="wrap">
    <div v-if="isMobile">
      <img
        v-if="accountData.isConnected && accountData.address" class="h-7 w-7" src="@img/common/account.svg" alt=""
        @click="handleModal"
      />
      <Button
        v-else data-testid="connect-wallet" class="p2 h-8 rounded-full border-none bg-[#FAF8F6] text-brand-900"
        label="Connect Wallet" @click="open({ view: 'Connect' })"
      />
    </div>
    <div v-else>
      <div
        v-if="accountData.isConnected && accountData.address" data-testid="wallet-address"
        class="p2 flex h-[44px] items-center justify-center gap-2 rounded-full border-none bg-black px-4 text-white"
        @click="handleModal"
      >
        <!-- <div v-if="pendingTxList.length > 0" class="font-medium">
          <div
            v-if="pendingTxList.length === 1"
            class="flex h-8 items-center gap-2 rounded-full bg-[#11A24D] px-1 text-white lg:px-3"
          >
            <i class="pi pi-check hidden lg:block"></i>
            <span>1 {{ t('common.success') }}</span>
          </div>
          <div
            v-else
            class="flex h-8 items-center gap-2 rounded-full bg-[#FFFFFF] px-1 text-brand-900 lg:px-3"
          >
            <i class="pi pi-spin pi-spinner hidden lg:block"></i>
            <span>1 Pending</span>
          </div>
        </div> -->

        <h4>
          {{ getAccountAddress(accountData.address) }}
        </h4>
        <img class="h-5 w-5" src="@img/common/account.svg" alt="" />
      </div>
      <div v-else>
        <Button
          data-testid="connect-wallet" class="rounded-full border-none bg-black text-white" label="Connect Wallet"
          @click="open({ view: 'Connect' })"
        />
      </div>
    </div>

    <ModalWrap :visible="accountModal.visible" @update:visible="accountStore.toggleModalVisible">
      <div class="p-4">
        <div class="relative flex items-center justify-center">
          <h3 class="h3">
            Account
          </h3>
          <Button
            class="absolute right-0 top-0" icon="pi pi-times" variant="text" rounded
            @click="accountStore.toggleModalVisible"
          />
        </div>
        <div class="mb-4 mt-6">
          <p class="p2 text-[#17171799]">
            Connected with MetaMask
          </p>
          <div class="mt-6 flex items-center justify-between gap-2">
            <div v-if="accountData.address" class="p1 flex items-center gap-2">
              <img src="@img/common/account.svg" class="h-5 w-5" />
              <span class="font-semibold">{{ getAccountAddress(accountData.address) }}</span>
              <a :href="blockExplorerUrl" target="_blank">
                <img src="@img/icon/link.svg" class="h-3 w-3" alt="" />
              </a>
            </div>
            <Button
              v-else data-testid="connect-wallet" class="rounded-full border-none bg-black text-white"
              label="Connect Wallet" @click="open({ view: 'Connect' })"
            />
            <div class="">
              <SwitchChainButton />
            </div>
          </div>
          <div
            v-if="accountData.address" class="p2 mt-6 flex cursor-pointer items-center gap-2 font-semibold"
            @click="copyAddress"
          >
            <img src="@img/icon/copy.svg" class="h-4 w-4" alt="" />
            <span>Copy address</span>
          </div>
        </div>
        <Divider class="my-6" />

        <div class="mt-6">
          <SelectButton
            :model-value="accountModal.type" :options="options" option-label="label" option-value="value"
            @update:model-value="accountStore.changeModal"
          />

          <div v-if="accountModal.type === 'wallet'" class="mt-6">
            <div v-for="token in tokens" :key="token.symbol" class="flex items-center justify-between rounded py-3">
              <div class="flex items-center gap-3">
                <img :src="getImageUrl(`token/${token.icon}`)" class="h-8 w-8" />
                <div>
                  <div class="font-semibold">
                    {{ token.title }}
                  </div>
                  <div class="p3">
                    ${{ price }}
                    <span class="text-green-500">{{ token.change }}</span>
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <div class="text-right">
                  <div class="font-semibold">
                    {{ token.balance }}{{ token.title }}
                  </div>
                  <div class="p3">
                    ≈${{ token.price }}
                  </div>
                </div>
                <i
                  v-if="token.address" v-tooltip.top="{
                    value: 'Add to MetaMask',
                    pt: {
                      arrow: {
                        style: {
                          backgroundColor: 'white',
                          display: 'none',
                        },
                      },
                      text: 'text-sm bg-white text-black',
                    },
                  }" class="pi pi-plus-circle cursor-pointer text-[#17171766]" @click="addToWallet(token)"
                ></i>
              </div>
            </div>
          </div>

          <div v-else class="my-6">
            <div
              v-for="(item, index) in txList" :key="item.hash"
              class="flex items-center justify-between border-b-[#8F92A11F] px-2 py-3"
              :class="{ 'border-b': index !== txList.length - 1 }"
            >
              <a
                v-if="item.type === 'bridge'" class="p3 flex items-center gap-3 text-brand-700" :href="item.exploreUrl"
                target="_blank"
              >
                <i class="pi pi-external-link text-xs font-bold"></i>
                <span>Bridge {{ item.amount }} WstASTR</span>
              </a>
              <a v-else class="p3 flex items-center gap-3 text-brand-700" :href="item.exploreUrl" target="_blank">
                <i class="pi pi-external-link text-xs font-bold"></i>
                <span>{{ item.type === 'stake' ? 'Stake' : 'Unstake' }} {{ item.amount }}
                  {{ item.type === 'stake' ? 'ASTR' : 'stAST' }} for
                  {{ round(Number(item.transferAmount), 2) }}
                  {{ item.type === 'stake' ? 'stAST' : 'ASTR' }}
                </span>
              </a>
              <div>
                <!-- <i class="pi pi-check text-sm font-bold text-[#11A24D]"></i> -->
              </div>
            </div>
          </div>
        </div>
        <Divider v-if="accountModal.type === 'wallet'" />
        <div
          data-testid="disconnect-wallet"
          class="p2 flex cursor-pointer items-center gap-3 rounded-[12px] p-3 font-semibold hover:bg-[#FAF8F6]"
          @click="handleDisconnect"
        >
          <img src="@img/icon/disconnect.svg" class="h-6 w-6" alt="" />
          <span>Disconnect</span>
        </div>
      </div>
    </ModalWrap>
  </div>
</template>

<style scoped lang="less">
:deep(.p-selectbutton) {
  border: 1px solid #000;
  border-radius: 8px;
  background-color: var(--p-surface-100);
  width: 100%;
  padding: 2px;

  .p-togglebutton {
    border-radius: 20px;
    width: 50%;

    .p-togglebutton-label {
      font-weight: 400;
    }
  }

  .p-togglebutton-checked {
    color: #fff;
    border-radius: 12px;

    &::before {
      background-color: #000;
      border-radius: 12px;
      height: 100%;
      width: 100%;
      top: 0;
      left: 0;
    }
  }
}

:deep(.switch-chain) {
  .content {
    border: 1px solid #171717;
    background: #faf8f6;
  }
}
</style>
